import { z } from 'zod';

export const cdlitAiBookingRoutingSchema = z
  .object({
    /** Один салон: все услуги → этот company id (если нет строки в serviceToCompany). */
    defaultCompanyId: z.string().min(1).optional(),
    serviceToCompany: z.record(z.string().min(1), z.string().min(1)),
    staffTitles: z.record(z.string().min(1), z.string().min(1)).optional(),
  })
  .strict();

export type CdlitAiBookingRouting = z.infer<typeof cdlitAiBookingRoutingSchema>;

/** Публичная видимость ассистента на landing-cdlit (не Telegram). */
export const cdlitSiteAssistantVisibilitySchema = z.enum(['off', 'admin_only', 'public']);
export type CdlitSiteAssistantVisibility = z.infer<typeof cdlitSiteAssistantVisibilitySchema>;

export const cdlitAiDecisionLayerModeSchema = z.enum(['off', 'shadow', 'active']);
export type CdlitAiDecisionLayerMode = z.infer<typeof cdlitAiDecisionLayerModeSchema>;

export const cdlitAiDecisionLayerSchema = z
  .object({
    mode: cdlitAiDecisionLayerModeSchema,
    provider: z.literal('openrouter'),
    model: z.string().trim().min(1).max(200),
    connectionKey: z.literal('default'),
    minConfidence: z.number().min(0).max(1),
    timeoutMs: z.number().int().min(100).max(10_000),
    policyVersion: z.string().trim().min(1).max(120),
  })
  .strict();

export type CdlitAiDecisionLayer = z.infer<typeof cdlitAiDecisionLayerSchema>;

export function defaultCdlitAiDecisionLayer(): CdlitAiDecisionLayer {
  return {
    mode: 'off',
    provider: 'openrouter',
    model: 'typesafe/jev-1.13',
    connectionKey: 'default',
    minConfidence: 0.9,
    timeoutMs: 1_200,
    policyVersion: 'routing-v1',
  };
}

export const cdlitAiCapabilityConnectionSourceSchema = z.enum(['direct_api', 'ai_platform']);
export type CdlitAiCapabilityConnectionSource = z.infer<typeof cdlitAiCapabilityConnectionSourceSchema>;

export const cdlitAiCapabilityConnectionRefSchema = z
  .object({
    source: cdlitAiCapabilityConnectionSourceSchema,
    providerId: z.string().trim().min(1).max(120),
    connectionKey: z.string().trim().min(1).max(120).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

export type CdlitAiCapabilityConnectionRef = z.infer<typeof cdlitAiCapabilityConnectionRefSchema>;

export const cdlitAiCapabilityProfileBindingSchema = z
  .object({
    primary: cdlitAiCapabilityConnectionRefSchema,
    fallback: cdlitAiCapabilityConnectionRefSchema.optional(),
  })
  .strict();

export type CdlitAiCapabilityProfileBinding = z.infer<typeof cdlitAiCapabilityProfileBindingSchema>;

export const cdlitAiCapabilityIdSchema = z.enum([
  'decision.route',
  'reasoning.simple',
  'reasoning.main',
  'reasoning.complex',
  'embeddings.documents',
  'rerank.documents',
  'vision.general',
  'document.extract',
  'speech.transcribe',
  'speech.diarize',
]);

export type CdlitAiCapabilityId = z.infer<typeof cdlitAiCapabilityIdSchema>;

export const cdlitAiCapabilityBindingsSchema = z.record(
  cdlitAiCapabilityIdSchema,
  cdlitAiCapabilityProfileBindingSchema,
);

export type CdlitAiCapabilityBindings = z.infer<typeof cdlitAiCapabilityBindingsSchema>;

export const cdlitAiProductSettingsSchema = z
  .object({
    /** Legacy; синхронизируется с `siteAssistantVisibility` при сохранении из админки. */
    aiEnabled: z.boolean(),
    availabilityEnabled: z.boolean(),
    bookingEnabled: z.boolean(),
    bookingRouting: cdlitAiBookingRoutingSchema,
    siteAssistantVisibility: cdlitSiteAssistantVisibilitySchema.optional(),
    decisionLayer: cdlitAiDecisionLayerSchema.optional(),
    capabilityBindings: cdlitAiCapabilityBindingsSchema.optional(),
  })
  .strict();

export type CdlitAiProductSettings = z.infer<typeof cdlitAiProductSettingsSchema>;

export function defaultCdlitAiProductSettings(): CdlitAiProductSettings {
  return {
    aiEnabled: false,
    availabilityEnabled: false,
    bookingEnabled: false,
    bookingRouting: { serviceToCompany: {} },
    siteAssistantVisibility: 'off',
    decisionLayer: defaultCdlitAiDecisionLayer(),
  };
}

export function resolveCdlitSiteAssistantVisibility(
  settings: CdlitAiProductSettings,
): CdlitSiteAssistantVisibility {
  if (settings.siteAssistantVisibility) {
    return settings.siteAssistantVisibility;
  }
  return settings.aiEnabled ? 'public' : 'off';
}

/** FAB и публичные session API для анонима / без админ-cookie. */
export function isCdlitSiteAssistantPubliclyVisible(settings: CdlitAiProductSettings): boolean {
  return resolveCdlitSiteAssistantVisibility(settings) === 'public';
}

export function isCdlitSiteAssistantOperationallyEnabled(settings: CdlitAiProductSettings): boolean {
  return resolveCdlitSiteAssistantVisibility(settings) !== 'off';
}

export function cdlitAiPublicGateEnabled(
  settings: CdlitAiProductSettings,
  landingAdminSession: boolean,
): boolean {
  const visibility = resolveCdlitSiteAssistantVisibility(settings);
  if (visibility === 'public') return true;
  if (visibility === 'admin_only' && landingAdminSession) return true;
  return false;
}

export function parseCdlitAiProductSettings(raw: unknown): CdlitAiProductSettings {
  const parsed = cdlitAiProductSettingsSchema.safeParse(raw);
  if (!parsed.success) {
    return defaultCdlitAiProductSettings();
  }
  const visibility = resolveCdlitSiteAssistantVisibility(parsed.data);
  return {
    ...parsed.data,
    siteAssistantVisibility: visibility,
    aiEnabled: visibility !== 'off',
    decisionLayer: parsed.data.decisionLayer ?? defaultCdlitAiDecisionLayer(),
  };
}

export function mergeCdlitAiProductSettings(
  base: CdlitAiProductSettings,
  patch: Partial<CdlitAiProductSettings>,
): CdlitAiProductSettings {
  const merged = {
    aiEnabled: patch.aiEnabled ?? base.aiEnabled,
    availabilityEnabled: patch.availabilityEnabled ?? base.availabilityEnabled,
    bookingEnabled: patch.bookingEnabled ?? base.bookingEnabled,
    bookingRouting: patch.bookingRouting ?? base.bookingRouting,
    siteAssistantVisibility: patch.siteAssistantVisibility ?? base.siteAssistantVisibility,
    decisionLayer: patch.decisionLayer ?? base.decisionLayer ?? defaultCdlitAiDecisionLayer(),
    capabilityBindings: patch.capabilityBindings ?? base.capabilityBindings,
  };
  const visibility = resolveCdlitSiteAssistantVisibility(merged);
  return {
    ...merged,
    siteAssistantVisibility: visibility,
    aiEnabled: visibility !== 'off',
  };
}

/** Public site gate — no secrets; drives FAB visibility + launcher identity. */
export const cdlitAiPublicStatusSchema = z
  .object({
    aiEnabled: z.boolean(),
    availabilityEnabled: z.boolean(),
    nativeBookingEnabled: z.boolean(),
    /** Guided panel.title — shown on launcher (e.g. «Афина»). */
    assistantName: z.string().min(1).max(200).optional(),
    /** Guided panel.lead — role/title (e.g. «Администратор»). */
    assistantTitle: z.string().min(1).max(200).optional(),
    /** Opening variants from guidedContent; teaser never owns separate public copy. */
    teaserGreetings: z
      .object({
        morning: z.array(z.string().min(1).max(4_000)),
        day: z.array(z.string().min(1).max(4_000)),
        evening: z.array(z.string().min(1).max(4_000)),
      })
      .strict()
      .optional(),
  })
  .strict();

export type CdlitAiPublicStatus = z.infer<typeof cdlitAiPublicStatusSchema>;
